import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit, Visibility } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  Switch,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CompanyVeiwController from "../../redux/actions/companyApiActions";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import AddAnnouncement from "./addAnnouncement";
import AnnouncementMobileView from "./AnnouncementMobileView";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ViewAnnouncementDialog from "./ViewAnnouncementDialog";
import ExportButton from "../../components/ag grid/ExportButton";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx, inputSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";
import { getUserInfoFromToken } from "../../utils/authUtils";

import "../../styles/styles.css";
import "../../styles/layout.css";

const EXPORT_COLUMNS = ["title", "message", "created_at", "active"];

const AnnouncementList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editAnnouncement, setEditAnnouncement] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState(null);

  const { getAnnouncement, updateAnnouncement, deleteAnnouncement } =
    CompanyVeiwController();
  const { announcement } = useSelector((state) => state.company);

  const canCreate =
    userType === "Admin" || hasPermission("notifications", "create");
  const canEdit =
    userType === "Admin" || hasPermission("notifications", "edit");
  const canDelete =
    userType === "Admin" || hasPermission("notifications", "delete");
  const canExport =
    userType === "Admin" || hasPermission("notifications", "export");

  useEffect(() => {
    dispatch(getAnnouncement());
  }, [dispatch]);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const filteredAnnouncements = useMemo(() => {
    if (!searchTerm.trim()) return announcement || [];
    const query = searchTerm.toLowerCase();
    return (announcement || []).filter(
      (item) =>
        item.title?.toLowerCase().includes(query) ||
        item.message?.toLowerCase().includes(query)
    );
  }, [announcement, searchTerm]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteAnnouncement(id)),
    [dispatch, deleteAnnouncement]
  );

  const handleView = useCallback((data) => {
    setViewData(data);
    setShowViewModal(true);
  }, []);

  const handleEdit = useCallback((data) => {
    setEditAnnouncement(data);
    setShowModal(true);
  }, []);

  const openAddModal = useCallback(() => {
    setEditAnnouncement(null);
    setShowModal(true);
  }, []);

  const handleToggleSelect = useCallback((item) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === item.id);
      if (exists) return prev.filter((row) => row.id !== item.id);
      return [...prev, item];
    });
  }, []);

  const handleStatusToggle = useCallback(
    (params) => {
      const updatedValue = !params.data.active;
      params.node.setDataValue("active", updatedValue);
      dispatch(
        updateAnnouncement(params.data.id, { ...params.data, active: updatedValue })
      );
    },
    [dispatch, updateAnnouncement]
  );

  const handleMobileToggleActive = useCallback(
    (item) => {
      dispatch(
        updateAnnouncement(item.id, { ...item, active: !item.active })
      );
    },
    [dispatch, updateAnnouncement]
  );

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) return;
    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      EXPORT_COLUMNS.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "announcements.xlsx");
  }, [selectedRows]);

  const refreshList = () => dispatch(getAnnouncement());

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Title",
        field: "title",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Message",
        field: "message",
        flex: 1,
        minWidth: 180,
        cellClass: "ag-cell-ellipsis",
        valueGetter: (params) => {
          const value = params.data.message || "";
          return value.length > 60 ? `${value.slice(0, 60)}...` : value;
        },
      },
      {
        headerName: "Created At",
        field: "created_at",
        width: 140,
        maxWidth: 150,
        minWidth: 140,
        suppressSizeToFit: true,
        valueFormatter: (params) => {
          if (!params.value) return "—";
          return new Date(params.value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Active",
        field: "active",
        width: 90,
        maxWidth: 100,
        minWidth: 90,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Switch
              checked={Boolean(params.value)}
              onChange={() => handleStatusToggle(params)}
              color="primary"
              size="small"
            />
          </div>
        ),
      },
      {
        headerName: "Actions",
        pinned: "right",
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleView(params.data)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(params.data)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "#d32f2f" }}
                  onClick={() => handleDelete(params.data.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [canEdit, canDelete, handleView, handleEdit, handleDelete, handleStatusToggle]
  );

  return (
    <>
      {isMobile ? (
        <AnnouncementMobileView
          announcements={filteredAnnouncements}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleMobileToggleActive}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          canExport={canExport}
        />
      ) : (
        <ListPageLayout
          title="Announcements"
          subtitle="Publish updates and notices for your organization"
          headerAction={
            canCreate ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutline />}
                onClick={openAddModal}
                sx={toolbarButtonSx}
              >
                Add Announcement
              </Button>
            ) : null
          }
          toolbarRight={
            <>
              {canExport && (
                <ExportButton
                  gridRef={gridRef}
                  fileName="announcements.xlsx"
                  exportColumns={EXPORT_COLUMNS}
                  disabled={selectedRows.length === 0}
                />
              )}
              <TextField
                size="small"
                placeholder="Search announcements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ ...inputSx, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              columnDefs={columnDefs}
              rowData={filteredAnnouncements}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              getRowId={(params) => String(params.data.id)}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No announcements yet"
              emptyHint={
                canCreate
                  ? 'Click "Add Announcement" to publish your first notice.'
                  : "No announcements are available."
              }
            />
          </div>
        </ListPageLayout>
      )}

      {showModal && (
        <AddAnnouncement
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editAnnouncement}
          refreshList={refreshList}
        />
      )}

      <ViewAnnouncementDialog
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        data={viewData}
      />
    </>
  );
};

export default AnnouncementList;
